import {observable, action} from 'mobx';
import {t} from 'shared/core';
import {types} from 'main/tools';
import {DomainStore} from 'shared/store';
import {successAlert} from 'shared/tools';
import _ from 'lodash';

class AdminContainerState {
  agent;
  match;
  history;
  store = new DomainStore();

  @observable cycle;
  @observable errors = {};

  @observable selectedReviewee = {};
  @observable reviewerSelectionModalOpen = false;
  @observable addRevieweeModalOpen = false;

  receiveProps({match, history, scope, cycle}) {
    this.match = match;
    this.history = history;
    this.scope = scope;
    this.cycle = cycle;
  }

  @action openReviewerSelectionModal(reviewee) {
    this.selectedReviewee = reviewee;
    this.reviewerSelectionModalOpen = true;
  }

  @action closeReviewerSelectionModal() {
    this.reviewerSelectionModalOpen = false;
  }

  @action onReviewersSelected() {
    this.interactiveAgent.refresh();
    this.closeReviewerSelectionModal();
  }

  @action openAddRevieweeModal() {
    this.addRevieweeModalOpen = true;
  }

  @action closeAddRevieweeModal() {
    this.addRevieweeModalOpen = false;
  }

  @action onRevieweeAdded() {
    this.interactiveAgent.refresh();
    this.closeAddRevieweeModal();
  }

  setInteractiveAgent(interactiveAgent) {
    this.interactiveAgent = interactiveAgent;
  }

  @action async endCycle() {
    const {model} = await this.store.post(
      this.cycle.link('end'),
      types.PERFORMANCE_REVIEWS.CYCLE,
      this.cycle
    );

    if (model) {
      this.cycle.merge(model);
      this.closeEndCycleModal();
    }
  }

  @action async updateDates() {
    const {model, errors} = await this.store.post(
      this.selectedReviewee.link('updateDates'),
      types.PERFORMANCE_REVIEWS.REVIEWEE,
      this.selectedReviewee.pick(['dueDate', 'launchDate'])
    );

    this.errors = errors;
    if (model) {
      this.interactiveAgent.refresh();
      this.closeEditDatesModal();
    }
  }

  @action async removeReviewee(reviewee) {
    await this.store.destroy(reviewee);
  }

  @action async shareReview(review) {
    if (!review) return this.closeShareWithRevieweeModal();

    const {model} = await this.store.post(
      review.link('share'),
      types.PERFORMANCE_REVIEWS.REVIEW
    );

    if (model) {
      this.interactiveAgent.refresh();
      this.closeShareWithRevieweeModal();
      successAlert(t('performance_reviews.view.alerts.Review shared.'));
    }
  }

  @action async unshareReview(review) {
    if (!review) return this.closeUnshareRevieweeModal();

    const {model} = await this.store.post(
      review.link('unshare'),
      types.PERFORMANCE_REVIEWS.REVIEW
    );

    if (model) {
      this.interactiveAgent.refresh();
      this.closeUnshareRevieweeModal();
      successAlert(t('performance_reviews.view.alerts.Review unshared.'));
    }
  }

  customLinksFor(reviewee) {
    const links = [];

    if (this.sharableReviewsFor(reviewee).length > 0) {
      links.push(
        {
          text: 'performance_reviews.view.Share',
          action: reviewee => this.openShareWithRevieweeModal(reviewee),
          order: 2
        }
      );
    }

    if (this.unsharableReviewsFor(reviewee).length > 0) {
      links.push(
        {
          text: 'performance_reviews.view.Unshare',
          action: reviewee => this.openUnshareRevieweeModal(reviewee),
          order: 2
        }
      );
    }

    if (reviewee.hasLink('updateDates')) {
      links.push(
        {
          text: 'performance_reviews.view.Edit Dates',
          action: reviewee => this.openEditDatesModal(reviewee),
          order: 1
        }
      );
    }

    return links;
  }

  sharableReviewsFor(reviewee) {
    return _.filter(reviewee.reviews, (review) => review.hasLink('share'));
  }
  
  unsharableReviewsFor(reviewee) {
    return _.filter(reviewee.reviews, (review) => review.hasLink('unshare'));
  }
}

export default AdminContainerState;
